import { WebOSApp, AppRegistry } from '../types/webos';

// Global app registry
const appRegistry: AppRegistry = new Map();

// Subscribers for registry changes
const subscribers: Set<() => void> = new Set();

// Subscribe to registry changes
export function subscribeToRegistry(callback: () => void): () => void {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

// Notify all subscribers of changes
function notifySubscribers(): void {
  subscribers.forEach(callback => callback());
}

// Register an app in the system
export function registerApp(app: WebOSApp): void {
  if (appRegistry.has(app.id)) {
    console.warn(`App with id "${app.id}" is already registered. Overwriting...`);
  }
  appRegistry.set(app.id, app);
  notifySubscribers();
}

// Unregister an app from the system
export function unregisterApp(appId: string): boolean {
  const result = appRegistry.delete(appId);
  if (result) {
    notifySubscribers();
  }
  return result;
}

// Get a specific app by ID
export function getApp(appId: string): WebOSApp | undefined {
  return appRegistry.get(appId);
}

// Get all registered apps
export function getAllApps(): WebOSApp[] {
  return Array.from(appRegistry.values());
}

// Get apps by category
export function getAppsByCategory(category: WebOSApp['category']): WebOSApp[] {
  return getAllApps().filter(app => app.category === category);
}

// Check if an app is registered
export function isAppRegistered(appId: string): boolean {
  return appRegistry.has(appId);
}

// Get app count
export function getAppCount(): number {
  return appRegistry.size;
}

// Clear all apps (useful for testing)
export function clearRegistry(): void {
  appRegistry.clear();
  notifySubscribers();
}

// Get apps sorted by name
export function getAppsSorted(): WebOSApp[] {
  return getAllApps().sort((a, b) => a.name.localeCompare(b.name));
}

// Bulk register apps
export function registerApps(apps: WebOSApp[]): void {
  apps.forEach(app => appRegistry.set(app.id, app));
  notifySubscribers();
}

// Export the registry for direct access if needed
export { appRegistry };